import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/client';
// eslint-disable-next-line no-restricted-imports
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useAuthSession } from '../hooks/useAuthSession';
import QuotaDisplay from '../components/billing/QuotaDisplay';
import EditableNotificationPreferences from '../components/profile/EditableNotificationPreferences';
import NotificationPreferencesNew from '../components/profile/NotificationPreferencesNew';
import TopQualifierSettings from '../components/topqualifier/TopQualifierSettings';
import CreditBalanceWidget from '../components/credits/CreditBalanceWidget';
import { BillingStatus } from '../types';
import { Link } from 'react-router-dom';
// Header is provided by AuthenticatedRoute wrapper

interface LeadExport {
  id: string;
  timestamp: string;
  row_count: number;
  cost: number;
  tier_breakdown: Record<string, number>;
  filters: Record<string, unknown>;
}

interface BillingEvent {
  id: string;
  created_at: string;
  amount: number;
  description: string;
  payment_method: string;
  status: string;
}

const ProfileSettings: React.FC = () => {
  const { billingStatus, isLoading, error } = useBillingStatus();
  const { session } = useAuthSession();
  // EPOCH 64: Added export_credits and outreach_credits tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'usage' | 'account' | 'export_credits' | 'outreach_credits'>('overview');
  const [useNewPreferences, setUseNewPreferences] = useState(false);

  const userRole = session?.user?.user_metadata?.role || 'user';
  const isCSR = userRole === 'csr';

  // Check feature flag for new preferences
  useEffect(() => {
    const checkFeatureFlag = async () => {
      if (!session?.user?.id) return;
      
      try {
        const { data } = await supabase
          .rpc('is_feature_enabled', {
            p_feature_name: 'new_email_preferences',
            p_user_id: session.user.id
          });
        
        if (data) {
          setUseNewPreferences(true);
        }
      } catch (error) {
        console.log('Using default preferences component');
      }
    };
    
    checkFeatureFlag();
  }, [session?.user?.id]);

  console.log('🔍 ProfileSettings: Rendering', {
    isLoading,
    hasError: !!error,
    hasBillingStatus: !!billingStatus,
    userRole,
    isCSR,
    activeTab
  });

  if (isLoading) {
    console.log('🔍 ProfileSettings: Showing loading state - useBillingStatus still loading');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">Failed to load profile information</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // EPOCH 64: Added Export Credits and Outreach Credits tabs
  // Filter tabs based on user role - CSRs don't see billing/usage/credits
  const allTabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'outreach_credits', label: 'Outreach Credits', icon: '💬' },
    { id: 'export_credits', label: 'Export Credits', icon: '📤' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'usage', label: 'Usage History', icon: '📈' },
    { id: 'account', label: 'Account', icon: '⚙️' }
  ] as const;
  
  const tabs = allTabs.filter(tab => {
    if (isCSR) {
      // CSRs only see overview and account tabs
      return tab.id === 'overview' || tab.id === 'account';
    }
    return true; // Regular users see all tabs
  }) as Array<{ id: 'overview' | 'billing' | 'usage' | 'account' | 'export_credits' | 'outreach_credits'; label: string; icon: string }>;

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
              <p className="text-gray-600">Manage your account and billing preferences</p>
            </div>
            
            {/* Quota Display in Header */}
            {billingStatus && (
              <QuotaDisplay variant="header" />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-3 text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-6">
                {activeTab === 'overview' && (
                  <OverviewTab
                    billingStatus={billingStatus}
                    isLoading={isLoading}
                    userId={session?.user?.id || ''}
                    onSwitchTab={(tab) => setActiveTab(tab)}
                  />
                )}
                {activeTab === 'export_credits' && (
                  <ExportCreditsTab billingStatus={billingStatus} isLoading={isLoading} userId={session?.user?.id || ''} />
                )}
                {activeTab === 'outreach_credits' && (
                  <OutreachCreditsTab userId={session?.user?.id || ''} />
                )}
                {activeTab === 'billing' && (
                  <BillingTab billingStatus={billingStatus} isLoading={isLoading} />
                )}
                {activeTab === 'usage' && (
                  <UsageHistoryTab />
                )}
                {activeTab === 'account' && (
                  <AccountTab
                    useNewPreferences={useNewPreferences}
                    sessionData={{
                      userId: session?.user?.id || '',
                      userRole: userRole,
                      email: session?.user?.email || ''
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to format plan expiration display
const getPlanExpirationDisplay = (
  trialEnabled: boolean | null,
  trialEndDate: string | null,
  subscriptionId: string | null,
  nextBillingDate: string | null,
  billingStatus: string | null
): string => {
  // Trial users
  if (trialEnabled && trialEndDate) {
    const expirationDate = new Date(trialEndDate);
    return expirationDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Offline paid users (subscription_id starts with 'offline_')
  if (subscriptionId?.startsWith('offline_') && nextBillingDate) {
    const paidThroughDate = new Date(nextBillingDate);
    return `Paid through ${paidThroughDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;
  }

  // Stripe monthly subscribers (subscription_id starts with 'sub_')
  if (subscriptionId?.startsWith('sub_')) {
    return 'Ongoing Monthly Subscription';
  }

  // No active subscription
  if (billingStatus !== 'active' && billingStatus !== 'internal') {
    return 'No active subscription';
  }

  // Fallback for internal users or edge cases
  return 'Contact support for details';
};

// Overview Tab Component
const OverviewTab: React.FC<{
  billingStatus: BillingStatus | null;
  isLoading: boolean;
  userId: string;
  onSwitchTab?: (tab: 'export_credits' | 'outreach_credits') => void;
}> = ({ billingStatus, isLoading, userId, onSwitchTab }) => {
  const [tqEnabled, setTqEnabled] = useState<boolean>(false);
  const [checkingTQ, setCheckingTQ] = useState(true);
  const [userTrialData, setUserTrialData] = useState<{
    trial_enabled: boolean | null;
    trial_end_date: string | null;
  }>({ trial_enabled: null, trial_end_date: null });

  useEffect(() => {
    const checkTQStatus = async () => {
      if (!userId) return;
      try {
        const { data } = await supabase
          .from('users')
          .select('tq_enabled, trial_enabled, trial_end_date')
          .eq('id', userId)
          .single();

        setTqEnabled(data?.tq_enabled || false);
        setUserTrialData({
          trial_enabled: data?.trial_enabled || null,
          trial_end_date: data?.trial_end_date || null,
        });
      } catch (error) {
        console.error('Error checking TQ status:', error);
      } finally {
        setCheckingTQ(false);
      }
    };

    checkTQStatus();
  }, [userId]);

  if (!billingStatus) {
    return <div className="text-center py-8 text-gray-500">No billing information available</div>;
  }

  // EPOCH 64: Removed Export Credits from Overview, added quick links to credit tabs
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Overview</h2>
      </div>

      {/* Current Plan - Full width */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Current Plan</h3>
        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600 capitalize">
                {billingStatus.planType?.replace('_', ' ') || '...'}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Status: <span className="capitalize">{billingStatus.billingStatus}</span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Plan Expiration: <span className="font-medium">
                  {getPlanExpirationDisplay(
                    userTrialData.trial_enabled,
                    userTrialData.trial_end_date,
                    billingStatus.subscriptionId,
                    billingStatus.nextBillingDate,
                    billingStatus.billingStatus
                  )}
                </span>
              </div>
            </div>
            <div className="text-3xl">💎</div>
          </div>
        )}
      </div>

      {/* Quick Links to Credit Tabs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Lead Generator Export Credits</h3>
            <span className="text-2xl">📤</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Credits for exporting leads from the Lead Generator tool.
          </p>
          <button
            onClick={() => onSwitchTab?.('export_credits')}
            className="text-purple-600 hover:text-purple-700 text-sm font-medium"
          >
            View Export Credits →
          </button>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">LinkedIn Outreach Credits</h3>
            <span className="text-2xl">💬</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Credits for LinkedIn outreach campaigns.
          </p>
          <button
            onClick={() => onSwitchTab?.('outreach_credits')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View Outreach Credits →
          </button>
        </div>
      </div>

      {/* Top Qualifier Section - Only show if enabled */}
      {!checkingTQ && tqEnabled && userId && (
        <TopQualifierSettings userId={userId} />
      )}

    </div>
  );
};

// EPOCH 64: Export Credits Tab Component
const ExportCreditsTab: React.FC<{ billingStatus: BillingStatus | null; isLoading: boolean; userId: string }> = ({ billingStatus, isLoading, userId }) => {
  const [exportHistory, setExportHistory] = useState<Array<{
    id: string;
    timestamp: string;
    row_count: number;
    cost: number;
  }>>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    const fetchExportHistory = async () => {
      if (!userId) return;
      try {
        setHistoryLoading(true);
        const { data, error } = await supabase
          .from('lead_exports')
          .select('id, created_at, row_count, cost')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        setExportHistory(data?.map(exp => ({
          id: exp.id,
          timestamp: exp.created_at,
          row_count: exp.row_count,
          cost: exp.cost
        })) || []);
      } catch (error) {
        console.error('Error fetching export history:', error);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchExportHistory();
  }, [userId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Lead Generator Export Credits</h2>
        <p className="text-gray-600">Manage your credits for exporting leads from the Lead Generator tool.</p>
      </div>

      {/* Credit Balance */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-100">
        <QuotaDisplay variant="page" showUpgradePrompt={true} />
      </div>

      {/* Export History */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Export History</h3>
        {historyLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : exportHistory.length > 0 ? (
          <div className="space-y-3">
            {exportHistory.map((exp) => (
              <div key={exp.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-xl">📊</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {exp.row_count.toLocaleString()} leads exported
                    </div>
                    <div className="text-xs text-gray-500">{formatDate(exp.timestamp)}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(exp.cost)}
                  </div>
                  {exp.cost === 0 && (
                    <div className="text-xs text-green-600">Free (quota)</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <span className="text-4xl mb-4 block">📊</span>
            <p>No export history yet.</p>
            <p className="text-sm mt-2">Your lead exports will appear here.</p>
          </div>
        )}
      </div>

      {/* Purchase More Credits */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">Need More Export Credits?</h3>
        <p className="text-sm text-blue-800 mb-4">
          Purchase additional credits to export more leads from the Lead Generator.
        </p>
        <Link
          to="/lead-generator"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Go to Lead Generator →
        </Link>
      </div>
    </div>
  );
};

// EPOCH 64: Outreach Credits Tab Component
const OutreachCreditsTab: React.FC<{ userId: string }> = ({ userId }) => {
  const [balanceData, setBalanceData] = useState<{
    balance: number;
    lifetime_purchased: number;
    lifetime_used: number;
    pricing_plan: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setError('Not authenticated');
          return;
        }

        // Check if user is on Platform or usage-based pricing
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('pricing_plan')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          console.error('Error fetching user data:', userError);
          setError('Failed to fetch user data');
          return;
        }

        // Only fetch balance for Platform or usage-based users
        if (userData.pricing_plan !== 'platform' && userData.pricing_plan !== 'usage_based') {
          setBalanceData(null);
          return;
        }

        // Fetch balance from Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-outreach-balance`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch balance: ${response.statusText}`);
        }

        const data = await response.json();
        setBalanceData({
          balance: data.balance,
          lifetime_purchased: data.lifetime_purchased,
          lifetime_used: data.lifetime_used,
          pricing_plan: data.pricing_plan,
        });
      } catch (error) {
        console.error('Error fetching outreach balance:', error);
        setError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [userId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn Outreach Credits</h2>
        </div>
        <div className="animate-pulse">
          <div className="h-48 bg-gray-200 rounded-lg mb-6"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  // User not on Platform/usage-based pricing
  if (!balanceData && !error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn Outreach Credits</h2>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8 text-center border border-blue-100">
          <span className="text-5xl mb-4 block">💬</span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Outreach Credits Not Available</h3>
          <p className="text-gray-600 mb-6">
            Outreach credits are available for Platform subscribers. Upgrade your plan to access LinkedIn outreach features.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
          >
            View Pricing Plans
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn Outreach Credits</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const { balance, lifetime_purchased, lifetime_used } = balanceData!;
  const usagePercentage = lifetime_purchased > 0
    ? Math.round((lifetime_used / lifetime_purchased) * 100)
    : 0;

  // Determine balance color
  const getBalanceColor = () => {
    if (balance === 0) return 'text-red-600';
    if (balance < 100) return 'text-orange-600';
    if (balance < 500) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">LinkedIn Outreach Credits</h2>
        <p className="text-gray-600">Manage your credits for LinkedIn outreach campaigns.</p>
      </div>

      {/* Balance Summary Card */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-purple-100 text-sm font-medium mb-2">Current Balance</p>
            <p className={`text-5xl font-bold ${balance < 100 ? 'text-yellow-300' : ''}`}>
              {balance.toLocaleString()}
            </p>
            <p className="text-purple-100 text-sm mt-2">credits available</p>
          </div>
          <div className="text-6xl opacity-20">💬</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-purple-500">
          <div>
            <p className="text-purple-100 text-sm">Lifetime Purchased</p>
            <p className="text-2xl font-semibold">{lifetime_purchased.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-100 text-sm">Lifetime Used</p>
            <p className="text-2xl font-semibold">{lifetime_used.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-100 text-sm">Usage Rate</p>
            <p className="text-2xl font-semibold">{usagePercentage}%</p>
          </div>
        </div>

        {/* Low Balance Warning */}
        {balance < 100 && balance > 0 && (
          <div className="mt-6 bg-yellow-500 bg-opacity-20 border border-yellow-300 rounded-lg p-4">
            <p className="text-yellow-100 font-medium">⚠️ Low balance! Consider purchasing more credits.</p>
          </div>
        )}

        {balance === 0 && (
          <div className="mt-6 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg p-4">
            <p className="text-red-100 font-medium">❌ No credits remaining! Purchase credits to continue.</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/credits"
          className="flex items-center justify-center px-6 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
        >
          <span className="mr-2">💳</span>
          Purchase More Credits
        </Link>
        <Link
          to="/credits"
          className="flex items-center justify-center px-6 py-4 bg-white text-purple-600 border-2 border-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold"
        >
          <span className="mr-2">📊</span>
          View Usage History
        </Link>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">💡 About Outreach Credits</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Each credit equals one outreach (connection request, message reply, etc.)</li>
          <li>• Credits never expire</li>
          <li>• Secure payment via Stripe</li>
          <li>• Credits are added instantly after payment</li>
        </ul>
      </div>
    </div>
  );
};

// Billing Tab Component
const BillingTab: React.FC<{ billingStatus: BillingStatus | null; isLoading: boolean }> = ({ billingStatus, isLoading }) => {
  const isInternalUser = billingStatus?.billingStatus === 'internal';
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing Information</h2>
      </div>

      {/* Special message for CSR/Admin users */}
      {isInternalUser && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                You have an internal account with full platform access. No billing charges apply to your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Details */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Current Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Account Type</label>
            <div className="text-lg font-medium">
              {isLoading ? (
                <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
              ) : (
                billingStatus?.planType || 'Free Account'
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <div className={`text-lg font-medium ${
              billingStatus?.billingStatus === 'internal' ? 'text-blue-600' : 
              billingStatus?.billingStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {billingStatus?.billingStatus === 'internal' ? 'Internal User' :
               billingStatus?.billingStatus === 'paid' ? 'Active' : 
               billingStatus?.billingStatus || 'Unknown'}
            </div>
          </div>
        </div>
      </div>

      {/* Export Credits Status */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Export Credits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Available Credits</label>
            <div className="text-2xl font-bold text-green-600">
              {billingStatus?.exportQuotaRemaining || 0}
            </div>
            <div className="text-sm text-gray-500">leads remaining</div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Quick Purchase</label>
            <div className="space-y-2">
              <button 
                onClick={() => window.location.href = '/lead-generator'}
                className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                Buy 1,000 leads - $50
              </button>
              <button 
                onClick={() => window.location.href = '/lead-generator'}
                className="w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                Custom Amount
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-gray-900">Payment Method</h3>
          {billingStatus?.paymentMethod && (
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm text-purple-600 border border-purple-600 rounded hover:bg-purple-50 transition-colors">
                Update
              </button>
              <button className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50 transition-colors">
                Remove
              </button>
            </div>
          )}
        </div>
        <div className="text-center py-6">
          <svg className="h-8 w-8 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="text-gray-600 mb-1">Payment methods are securely managed through Stripe</p>
          <p className="text-sm text-gray-500">Your payment information is encrypted and never stored on our servers</p>
        </div>
      </div>

      {/* Billing Summary */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Billing Summary</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Account Status</span>
            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
              billingStatus?.billingStatus === 'paid' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {billingStatus?.billingStatus === 'paid' ? 'Active' : 'Free Tier'}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Service Tier</span>
            <span className="text-sm font-medium">
              {billingStatus?.serviceTier === 'junior_sdr' ? 'Junior SDR - $400/month' : 
               billingStatus?.serviceTier === 'top_qualifier' ? 'Top Qualifier SDR - $2000/month' : 
               'Free Account'}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Billing Cycle</span>
            <span className="text-sm font-medium">{billingStatus?.serviceTier ? 'Monthly' : 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-600">Lead Credits Remaining</span>
            <span className="text-sm font-medium">{billingStatus?.exportQuotaRemaining || 0} leads</span>
          </div>
        </div>
      </div>

    </div>
  );
};

// Usage History Tab Component
const UsageHistoryTab: React.FC = () => {
  const [exportHistory, setExportHistory] = useState<LeadExport[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState<'exports' | 'billing'>('exports');

  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Fetch export history
      const { data: exports, error: exportError } = await supabase
        .from('lead_exports')
        .select('*')
        .eq('user_id', session.user.id)
        .order('timestamp', { ascending: false })
        .limit(20);

      if (exportError) throw exportError;

      // Process export data
      const processedExports = (exports || []).map(exportItem => ({
        ...exportItem,
        tier_breakdown: typeof exportItem.tier_breakdown === 'string' 
          ? JSON.parse(exportItem.tier_breakdown) 
          : exportItem.tier_breakdown,
        filters: typeof exportItem.filters === 'string' 
          ? JSON.parse(exportItem.filters) 
          : exportItem.filters,
      }));

      setExportHistory(processedExports);

      // Fetch billing history
      const { data: billing, error: billingError } = await supabase
        .from('billing_events')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (billingError && billingError.code !== 'PGRST116') { // Ignore table doesn't exist error
        console.error('Billing history error:', billingError);
      }

      setBillingHistory(billing || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const calculateUsageStats = () => {
    const totalExports = exportHistory.length;
    const totalLeads = exportHistory.reduce((sum, exp) => sum + exp.row_count, 0);
    const totalSpent = exportHistory.reduce((sum, exp) => sum + exp.cost, 0);
    const freeExports = exportHistory.filter(exp => exp.cost === 0).length;
    const paidExports = exportHistory.filter(exp => exp.cost > 0).length;

    return {
      totalExports,
      totalLeads,
      totalSpent,
      freeExports,
      paidExports
    };
  };

  const stats = calculateUsageStats();

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage History</h2>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading usage history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage History</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Usage Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.totalExports}</div>
          <div className="text-sm text-blue-800">Total Exports</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{stats.totalLeads.toLocaleString()}</div>
          <div className="text-sm text-green-800">Leads Exported</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalSpent)}</div>
          <div className="text-sm text-purple-800">Total Spent</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">{stats.freeExports}</div>
          <div className="text-sm text-orange-800">Free Exports</div>
        </div>
      </div>

      {/* Usage Trends Visualization */}
      {exportHistory.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Trends</h3>
          <div className="space-y-4">
            {/* Free vs Paid Exports Ratio */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Export Types</span>
                <span className="text-sm text-gray-500">
                  {stats.freeExports} free, {stats.paidExports} paid
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full flex"
                  style={{ width: '100%' }}
                >
                  <div 
                    className="bg-green-500 h-3 rounded-l-full"
                    style={{ 
                      width: stats.totalExports > 0 ? `${(stats.freeExports / stats.totalExports) * 100}%` : '0%' 
                    }}
                  ></div>
                  <div 
                    className="bg-blue-500 h-3 rounded-r-full"
                    style={{ 
                      width: stats.totalExports > 0 ? `${(stats.paidExports / stats.totalExports) * 100}%` : '0%' 
                    }}
                  ></div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>Free (Quota)</span>
                <span>Paid</span>
              </div>
            </div>

            {/* Average Export Size */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Average Export Size</span>
                <span className="text-sm text-gray-500">
                  {stats.totalExports > 0 ? Math.round(stats.totalLeads / stats.totalExports) : 0} leads per export
                </span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {exportHistory.slice(0, 5).map((exp, index) => (
                  <div key={index} className="text-center">
                    <div 
                      className="bg-blue-200 rounded-t"
                      style={{ 
                        height: `${Math.min((exp.row_count / Math.max(...exportHistory.map(e => e.row_count))) * 40 + 10, 50)}px` 
                      }}
                    ></div>
                    <div className="text-xs text-gray-600 mt-1">
                      {exp.row_count}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <span className="text-sm font-medium text-gray-700">Recent Activity</span>
              <div className="mt-2 flex items-center space-x-2">
                {exportHistory.slice(0, 10).map((exp, index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full ${
                      exp.cost > 0 ? 'bg-blue-400' : 'bg-green-400'
                    }`}
                    title={`${exp.row_count} leads - ${formatDate(exp.timestamp)}`}
                  ></div>
                ))}
                {exportHistory.length === 0 && (
                  <div className="text-sm text-gray-500">No recent exports</div>
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>🟢 Free</span>
                <span>🔵 Paid</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveHistoryTab('exports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeHistoryTab === 'exports'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Export History ({exportHistory.length})
          </button>
          <button
            onClick={() => setActiveHistoryTab('billing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeHistoryTab === 'billing'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Billing History ({billingHistory.length})
          </button>
        </nav>
      </div>

      {/* Export History */}
      {activeHistoryTab === 'exports' && (
        <div>
          {exportHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No export history found.
            </div>
          ) : (
            <div className="space-y-3">
              {exportHistory.map((exportItem, index) => (
                <div key={exportItem.id || index} className="bg-white border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {exportItem.row_count} leads exported
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(exportItem.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(exportItem.cost)}
                      </div>
                      {exportItem.cost === 0 && (
                        <div className="text-xs text-green-600">Free (quota)</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Billing History */}
      {activeHistoryTab === 'billing' && (
        <div>
          {billingHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No billing history found.
            </div>
          ) : (
            <div className="space-y-3">
              {billingHistory.map((billingEvent, index) => (
                <div key={billingEvent.id || index} className="bg-white border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {billingEvent.event_type.replace('_', ' ')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(billingEvent.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(billingEvent.amount_paid)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Account Tab Component
interface AccountTabProps {
  useNewPreferences: boolean;
  sessionData: {
    userId: string;
    userRole: string;
    email: string;
  };
}

const AccountTab: React.FC<AccountTabProps> = ({ useNewPreferences, sessionData }) => {
  // Using sessionData from parent instead of duplicate hook
  const [userProfile, setUserProfile] = useState<{
    full_name?: string;
    user_display_id?: string;
    email?: string;
    role?: string;
    profile_completed?: boolean;
    whatsapp_number?: string;
    line_id?: string;
    joined_company_at?: string;
    notification_preferences?: {
      company_updates?: string;
      verification_requests?: string;
      system_notifications?: string;
      [key: string]: string | boolean | undefined;
    };
  } | null>(null);
  const [companyInfo, setCompanyInfo] = useState<{
    id: string;
    name: string;
    domain?: string;
    max_users?: number;
    subscription_tier?: string;
    allow_public_join?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<typeof userProfile>(null);

  useEffect(() => {
    if (sessionData.userId) {
      fetchUserProfile();
    }
  }, [sessionData.userId]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      if (!sessionData.userId) {
        console.log('No user ID available');
        return;
      }

      console.log('Fetching profile for user:', sessionData.userId);

      // First, fetch user profile data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.userId)
        .single();

      if (userError) {
        console.error('User query error:', userError);
        throw userError;
      }

      console.log('User data fetched:', userData);
      setUserProfile(userData);

      // Then fetch company info separately if company_id exists
      if (userData?.company_id) {
        console.log('Fetching company for ID:', userData.company_id);
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id, name, domain, max_users, subscription_tier, allow_public_join')
          .eq('id', userData.company_id)
          .single();

        if (companyError) {
          console.error('Company query error:', companyError);
          // Don't throw - just log the error and continue without company data
        } else {
          console.log('Company data fetched:', companyData);
          setCompanyInfo(companyData);
        }
      } else {
        console.log('No company_id found for user');
      }

    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button 
            onClick={fetchUserProfile}
            className="mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
      </div>

      {/* Profile Information */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Full Name</label>
            <div className="text-lg font-medium text-gray-900">
              {userProfile?.full_name || 'Not set'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">User Display ID</label>
            <div className="text-lg font-medium text-gray-900 font-mono">
              {userProfile?.user_display_id || 'Not set'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Email Address</label>
            <div className="text-lg font-medium text-gray-900">
              {userProfile?.email || 'Not available'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Profile Status</label>
            <div className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${
              userProfile?.profile_completed 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {userProfile?.profile_completed ? 'Complete' : 'Incomplete'}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-600 mb-1">WhatsApp Number</label>
            <div className="text-lg font-medium text-gray-900">
              {userProfile?.whatsapp_number || 'Not provided'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Line ID</label>
            <div className="text-lg font-medium text-gray-900">
              {userProfile?.line_id || 'Not provided'}
            </div>
          </div>
        </div>
      </div>

      {/* Company Information */}
      {companyInfo && (
        <div className="border rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-4">Company Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Company Name</label>
              <div className="text-lg font-medium text-gray-900">
                {companyInfo.name}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Company Domain</label>
              <div className="text-lg font-medium text-gray-900">
                {companyInfo.domain || 'Not set'}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Subscription Tier</label>
              <div className="text-lg font-medium text-gray-900 capitalize">
                {companyInfo.subscription_tier || 'Not set'}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Maximum Users</label>
              <div className="text-lg font-medium text-gray-900">
                {companyInfo.max_users || 'Unlimited'}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Member Since</label>
              <div className="text-lg font-medium text-gray-900">
                {userProfile?.joined_company_at 
                  ? new Date(userProfile.joined_company_at).toLocaleDateString()
                  : 'Not available'
                }
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Public Join</label>
              <div className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${
                companyInfo.allow_public_join 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {companyInfo.allow_public_join ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Preferences */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          {sessionData.userId && (
            useNewPreferences ? (
              <NotificationPreferencesNew
                userId={sessionData.userId}
                userRole={sessionData.userRole as 'user' | 'csr' | 'admin'}
              />
            ) : (
              <EditableNotificationPreferences
                userId={sessionData.userId}
                currentPreferences={userProfile?.notification_preferences || {}}
                userRole={sessionData.userRole}
              />
            )
          )}
        </div>
      </div>

      {/* Account Actions */}
      <div className="border rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Account Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button 
            onClick={() => {
              setEditMode(true);
              setEditedProfile(userProfile);
            }}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Profile
          </button>
          <button 
            onClick={async () => {
              try {
                const { error } = await supabase.auth.resetPasswordForEmail(
                  userProfile?.email || '',
                  { redirectTo: `${window.location.origin}/reset-password` }
                );
                if (error) throw error;
                alert('Password reset email has been sent to your email address.');
              } catch (err) {
                console.error('Error sending password reset:', err);
                alert('Failed to send password reset email. Please try again.');
              }
            }}
            className="flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v-2L4.257 9.257a6 6 0 119.486-1.514L15 7z" />
            </svg>
            Change Password
          </button>
          <button 
            onClick={() => {
              // Navigate to pricing page for plan selection
              window.location.href = '/pricing';
            }}
            className="flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Change Plan
          </button>
          <button 
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-red-600 mb-4">Delete Account</h2>
            <p className="text-gray-700 mb-4">
              <strong>Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
            </p>
            <p className="text-gray-700 mb-4">
              To confirm deletion, please type <strong>DELETE</strong> below:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="Type DELETE to confirm"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteConfirmText !== 'DELETE') {
                    alert('Please type DELETE to confirm account deletion.');
                    return;
                  }
                  setIsDeleting(true);
                  try {
                    // Delete user account
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;
                    
                    // First delete user data from database
                    const { error: dbError } = await supabase
                      .from('users')
                      .delete()
                      .eq('id', sessionData.userId);
                    
                    if (dbError) throw dbError;
                    
                    // Then sign out
                    await supabase.auth.signOut();
                    
                    // Redirect to home page
                    window.location.href = '/';
                  } catch (err) {
                    console.error('Error deleting account:', err);
                    alert('Failed to delete account. Please contact support.');
                    setIsDeleting(false);
                  }
                }}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className={`px-4 py-2 rounded-lg ${
                  deleteConfirmText === 'DELETE' && !isDeleting
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editedProfile?.full_name || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, full_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username (User Display ID)</label>
                <input
                  type="text"
                  value={editedProfile?.user_display_id || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, user_display_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                  placeholder="e.g., john_doe123"
                  maxLength={20}
                />
                <p className="text-xs text-gray-500 mt-1">3-20 characters, lowercase letters, numbers, underscores, and hyphens only</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                <input
                  type="text"
                  value={editedProfile?.whatsapp_number || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Line ID</label>
                <input
                  type="text"
                  value={editedProfile?.line_id || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, line_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditedProfile(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;
                    
                    // Validate username if changed
                    if (editedProfile?.user_display_id !== userProfile?.user_display_id) {
                      // Check if username is valid length
                      if (!editedProfile?.user_display_id || editedProfile.user_display_id.length < 3) {
                        alert('Username must be at least 3 characters long');
                        return;
                      }
                      
                      // Check if username is available
                      const { data: existingUser } = await supabase
                        .from('users')
                        .select('id')
                        .eq('user_display_id', editedProfile.user_display_id)
                        .neq('id', sessionData.userId)
                        .single();
                      
                      if (existingUser) {
                        alert('This username is already taken. Please choose another.');
                        return;
                      }
                    }
                    
                    const { error } = await supabase
                      .from('users')
                      .update({
                        full_name: editedProfile?.full_name,
                        user_display_id: editedProfile?.user_display_id,
                        whatsapp_number: editedProfile?.whatsapp_number,
                        line_id: editedProfile?.line_id,
                      })
                      .eq('id', sessionData.userId);
                    
                    if (error) throw error;
                    
                    setUserProfile(editedProfile);
                    setEditMode(false);
                    alert('Profile updated successfully!');
                  } catch (err) {
                    console.error('Error updating profile:', err);
                    alert('Failed to update profile. Please try again.');
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;